import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'

import { fetchContractorPayment, fetchContractors } from '../payrollActions'
import { selectContractorByUuid } from '../payroll.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Link, Text, Card, Separator, CardColors } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { Gusto_ContractorPayment } from '../generated_gusto_types'
import { TypographyStyles } from '@/styles'
import { ContactSupport } from './ContactSupport'
import { ContractorPaymentTable } from '../run-payroll/contractor-payroll/ContractorPaymentTable'
import currency from 'currency.js'

const formatDate = (date: string) =>
  moment(date).format(DATE_FORMATS.DISPLAY_SHORT)

const PayrollOverview = ({
  payment,
  total,
}: {
  payment: Gusto_ContractorPayment | undefined
  total: number
}) => {
  if (!payment) {
    return null
  }
  return (
    <Card color={CardColors.EMPHASIS}>
      <Text variant={TypographyStyles.H1}>Payroll Details</Text>

      <Separator />

      <div className="flex justify-between items-start">
        <b>Funds Debited :</b>
        <Text variant={TypographyStyles.BODY_MD}>{formatCurrency(total)}</Text>
      </div>

      <Separator />

      <div className="flex justify-between items-start">
        <Text variant={TypographyStyles.BODY_MD}>
          <b>Pay Date: </b>
        </Text>
        <Text variant={TypographyStyles.BODY_MD}>
          {formatDate(payment.date)}
        </Text>
      </div>
    </Card>
  )
}

export const PayrollHistoryContractorDetails = () => {
  const dispatch = useAppDispatch()
  const [payment, setPayment] = useState<Gusto_ContractorPayment>()
  const contractor = useReselector(
    selectContractorByUuid,
    payment?.contractor_uuid
  )

  const { uuid } = useParams()

  useEffect(() => {
    const fetch = async () => {
      if (!uuid) {
        return
      }
      const res = await fetchContractorPayment(uuid)(dispatch)

      if (res) {
        setPayment(res)
      }
    }
    fetch()
    dispatch(fetchContractors())
  }, [dispatch, uuid])

  const total = payment
    ? currency(payment.wage_total).add(payment.reimbursement).dollars()
    : 0

  return (
    <Card>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H1}>Payroll History</Text>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Link href="/payroll/history">Payroll History</Link>
          {payment && (
            <Text variant={TypographyStyles.BODY_MD} className="pt-[3px]">
              <b>
                &nbsp;&rsaquo;&nbsp;<b>Contractor Payment:</b>{' '}
                {moment(payment.date).format(DATE_FORMATS.DISPLAY_SHORT)}
              </b>
            </Text>
          )}
        </div>

        <Separator />

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-7/12">
            <PayrollOverview payment={payment} total={total} />
          </div>
          <div className="lg:w-5/12">
            <ContactSupport />
          </div>
        </div>

        {payment && contractor && (
          <ContractorPaymentTable
            contractor={contractor}
            hours={payment.hours}
            bonus={payment.bonus}
            wages={payment.wage_total}
            reimbursement={payment.reimbursement}
            total={total}
          />
        )}
      </div>
    </Card>
  )
}
